import { clavePersonaje } from './personajes-parecidos';

export interface CastRow {
  name?: string | null;
  type?: string | null;
  dialogue_lines?: number | null;
  scene_count?: number | null;
  scenes?: Array<number | string> | null;
}

export interface CharacterTotals {
  key: string;
  name: string;
  types: string[];
  dialogueLines: number;
  sceneCount: number;
  scenes: string[];
}

interface BreakdownScene {
  scene_number?: number | string | null;
  breakdown?: { cast?: Array<{ name?: string | null; type?: string | null; dialogue_lines?: number | null }> | null } | null;
}

export function mergeCastByCharacter(rows: CastRow[] | null | undefined): CharacterTotals[] {
  const byKey = new Map<string, { name: string; types: Set<string>; dialogueLines: number; counted: number; scenes: Set<string> }>();
  for (const row of rows ?? []) {
    const key = clavePersonaje(row.name);
    if (!key) continue;
    const entry = byKey.get(key) ?? { name: key, types: new Set<string>(), dialogueLines: 0, counted: 0, scenes: new Set<string>() };
    if (row.type) entry.types.add(row.type);
    entry.dialogueLines += Number(row.dialogue_lines) || 0;
    entry.counted = Math.max(entry.counted, Number(row.scene_count) || 0);
    for (const s of row.scenes ?? []) entry.scenes.add(String(s));
    byKey.set(key, entry);
  }
  return [...byKey.entries()].map(([key, e]) => ({
    key,
    name: e.name,
    types: [...e.types],
    dialogueLines: e.dialogueLines,
    sceneCount: e.scenes.size > 0 ? e.scenes.size : e.counted,
    scenes: [...e.scenes],
  }));
}

export function castFromBreakdowns(scenes: BreakdownScene[] | null | undefined): CastRow[] {
  const rows: CastRow[] = [];
  for (const scene of scenes ?? []) {
    for (const c of scene.breakdown?.cast ?? []) {
      rows.push({
        name: c?.name,
        type: c?.type,
        dialogue_lines: c?.dialogue_lines,
        scenes: scene.scene_number == null ? [] : [scene.scene_number],
      });
    }
  }
  return rows;
}

export function projectCharacters(castRows: CastRow[] | null | undefined, scenes?: BreakdownScene[] | null): CharacterTotals[] {
  const fromCast = mergeCastByCharacter(castRows);
  return fromCast.length > 0 ? fromCast : mergeCastByCharacter(castFromBreakdowns(scenes));
}

export function countCharacters(characters: CharacterTotals[]): number {
  return characters.length;
}

export function countCharactersWithDialogue(characters: CharacterTotals[]): number {
  return characters.filter((c) => c.dialogueLines > 0).length;
}

export function sceneCountByCharacter(characters: CharacterTotals[]): Map<string, number> {
  return new Map(characters.map((c) => [c.key, c.sceneCount]));
}

export function sceneCountFor(counts: Map<string, number>, name: unknown): number {
  return counts.get(clavePersonaje(name)) ?? 0;
}

export function sceneIdsByCharacter(
  characters: CharacterTotals[],
  scenes: Array<{ id: string; scene_number?: number | string | null }> | null | undefined,
): Record<string, string[]> {
  const idByNumber = new Map<string, string>();
  for (const s of scenes ?? []) {
    if (s.scene_number != null) idByNumber.set(String(s.scene_number), s.id);
  }
  const out: Record<string, string[]> = {};
  for (const c of characters) {
    out[c.key] = c.scenes.map((n) => idByNumber.get(n)).filter((id): id is string => !!id);
  }
  return out;
}
